/**
 * OWL2 Data Range Types
 *
 * Defines data ranges used in OWL2 for constraining data values.
 * Includes datatype references, boolean combinations, enumerations,
 * and datatype restrictions with facets.
 */

import type { IRI } from './IRI.ts';
import type { Literal } from './Literal.ts';

// =============================================================================
// Data Range Type Definitions
// =============================================================================

/**
 * All possible data range types.
 */
export type DataRangeType =
    | 'Datatype'
    | 'DataIntersectionOf'
    | 'DataUnionOf'
    | 'DataComplementOf'
    | 'DataOneOf'
    | 'DatatypeRestriction';

/**
 * Base interface for all data ranges.
 */
interface DataRangeBase {
    readonly type: DataRangeType;
}

/**
 * Named datatype - references a datatype by IRI.
 * Example: xsd:integer, xsd:string
 */
export interface Datatype extends DataRangeBase {
    readonly type: 'Datatype';
    readonly iri: IRI;
}

/**
 * Intersection of data ranges (AND).
 * All constraints must be satisfied.
 * Requires at least 2 operands.
 */
export interface DataIntersectionOf extends DataRangeBase {
    readonly type: 'DataIntersectionOf';
    readonly dataRanges: readonly [DataRange, DataRange, ...DataRange[]];
}

/**
 * Union of data ranges (OR).
 * At least one constraint must be satisfied.
 * Requires at least 2 operands.
 */
export interface DataUnionOf extends DataRangeBase {
    readonly type: 'DataUnionOf';
    readonly dataRanges: readonly [DataRange, DataRange, ...DataRange[]];
}

/**
 * Complement of a data range (NOT).
 * Values NOT in the specified data range.
 */
export interface DataComplementOf extends DataRangeBase {
    readonly type: 'DataComplementOf';
    readonly dataRange: DataRange;
}

/**
 * Enumeration of literal values.
 * Value must be one of the specified literals.
 * Requires at least 1 literal.
 */
export interface DataOneOf extends DataRangeBase {
    readonly type: 'DataOneOf';
    readonly literals: readonly [Literal, ...Literal[]];
}

/**
 * Facet restriction for constraining datatype values.
 * Example: xsd:minInclusive = 0
 */
export interface FacetRestriction {
    /** The constraining facet IRI (e.g., xsd:minInclusive) */
    readonly facet: IRI;
    /** The restricting value */
    readonly value: Literal;
}

/**
 * Datatype with facet restrictions.
 * Constrains a base datatype with one or more facet restrictions.
 * Example: xsd:integer with minInclusive=0, maxExclusive=100
 */
export interface DatatypeRestriction extends DataRangeBase {
    readonly type: 'DatatypeRestriction';
    /** The base datatype being restricted */
    readonly datatype: Datatype;
    /** At least one facet restriction */
    readonly facetRestrictions: readonly [FacetRestriction, ...FacetRestriction[]];
}

/**
 * Discriminated union of all data range types.
 * The 'type' field distinguishes between data range kinds.
 */
export type DataRange =
    | Datatype
    | DataIntersectionOf
    | DataUnionOf
    | DataComplementOf
    | DataOneOf
    | DatatypeRestriction;

// =============================================================================
// Factory Functions
// =============================================================================

/**
 * Create a named datatype reference.
 */
export function datatype(iri: IRI): Datatype {
    return { type: 'Datatype', iri };
}

/**
 * Create an intersection of data ranges.
 * Requires at least 2 data ranges.
 */
export function dataIntersectionOf(
    first: DataRange,
    second: DataRange,
    ...rest: DataRange[]
): DataIntersectionOf {
    return {
        type: 'DataIntersectionOf',
        dataRanges: [first, second, ...rest]
    };
}

/**
 * Create a union of data ranges.
 * Requires at least 2 data ranges.
 */
export function dataUnionOf(
    first: DataRange,
    second: DataRange,
    ...rest: DataRange[]
): DataUnionOf {
    return {
        type: 'DataUnionOf',
        dataRanges: [first, second, ...rest]
    };
}

/**
 * Create a complement of a data range.
 */
export function dataComplementOf(dataRange: DataRange): DataComplementOf {
    return { type: 'DataComplementOf', dataRange };
}

/**
 * Create an enumeration of literal values.
 * Requires at least 1 literal.
 */
export function dataOneOf(first: Literal, ...rest: Literal[]): DataOneOf {
    return {
        type: 'DataOneOf',
        literals: [first, ...rest]
    };
}

/**
 * Create a facet restriction.
 */
export function facetRestriction(facet: IRI, value: Literal): FacetRestriction {
    return { facet, value };
}

/**
 * Create a datatype with facet restrictions.
 * Requires at least 1 facet restriction.
 */
export function datatypeRestriction(
    datatypeRef: Datatype,
    firstRestriction: FacetRestriction,
    ...restRestrictions: FacetRestriction[]
): DatatypeRestriction {
    return {
        type: 'DatatypeRestriction',
        datatype: datatypeRef,
        facetRestrictions: [firstRestriction, ...restRestrictions]
    };
}

// =============================================================================
// Type Guards
// =============================================================================

/**
 * Check if a data range is a named datatype.
 */
export function isDatatype(dr: DataRange): dr is Datatype {
    return dr.type === 'Datatype';
}

/**
 * Check if a data range is an intersection.
 */
export function isDataIntersectionOf(dr: DataRange): dr is DataIntersectionOf {
    return dr.type === 'DataIntersectionOf';
}

/**
 * Check if a data range is a union.
 */
export function isDataUnionOf(dr: DataRange): dr is DataUnionOf {
    return dr.type === 'DataUnionOf';
}

/**
 * Check if a data range is a complement.
 */
export function isDataComplementOf(dr: DataRange): dr is DataComplementOf {
    return dr.type === 'DataComplementOf';
}

/**
 * Check if a data range is an enumeration.
 */
export function isDataOneOf(dr: DataRange): dr is DataOneOf {
    return dr.type === 'DataOneOf';
}

/**
 * Check if a data range is a datatype restriction.
 */
export function isDatatypeRestriction(dr: DataRange): dr is DatatypeRestriction {
    return dr.type === 'DatatypeRestriction';
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get all datatype IRIs referenced in a data range (recursive).
 */
export function getDatatypeIRIs(dr: DataRange): IRI[] {
    switch (dr.type) {
        case 'Datatype':
            return [dr.iri];
        case 'DataIntersectionOf':
        case 'DataUnionOf':
            return dr.dataRanges.flatMap(getDatatypeIRIs);
        case 'DataComplementOf':
            return getDatatypeIRIs(dr.dataRange);
        case 'DataOneOf':
            return [];
        case 'DatatypeRestriction':
            return [dr.datatype.iri];
    }
}
